import React from 'react'

export function Badge({ children, variant = 'default' }) {
  const styles = {
    success: { background: '#EAF3DE', color: '#3B6D11' },
    danger:  { background: '#FCEBEB', color: '#A32D2D' },
    warning: { background: '#FAEEDA', color: '#854F0B' },
    info:    { background: '#E6F1FB', color: '#185FA5' },
    gray:    { background: '#F1EFE8', color: '#5F5E5A' },
    default: { background: '#F1EFE8', color: '#5F5E5A' },
  }
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 99,
      fontSize: 11, fontWeight: 500, ...styles[variant]
    }}>
      {children}
    </span>
  )
}

export function StatusBadge({ status }) {
  const map = {
    'Qualified':        'success',
    'Closed Won':       'success',
    'Engaged':          'info',
    'Meeting Scheduled':'info',
    'Demo Scheduled':   'success',
    'Contacted':        'gray',
    'Not Interested':   'danger',
    'Closed Lost':      'danger',
    'Follow Up Later':  'warning',
    'Not Yet Contacted':'gray',
    'Proposal Sent':    'warning',
    'Negotiation':      'danger',
  }
  return <Badge variant={map[status] || 'gray'}>{status}</Badge>
}

export function Card({ children, style }) {
  return (
    <div style={{
      background: '#fff', border: '0.5px solid rgba(0,0,0,0.12)',
      borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1rem',
      ...style
    }}>
      {children}
    </div>
  )
}

export function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase',
      letterSpacing: '0.05em', marginBottom: 12
    }}>
      {children}
    </div>
  )
}

export function MetricCard({ label, value, sub, valueColor }) {
  return (
    <div style={{
      background: '#f9f9f7', borderRadius: 8, padding: '0.85rem',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: valueColor || '#111' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#aaa', marginTop: 3 }}>{sub}</div>}
    </div>
  )
}

export function AgentAvatar({ agent, AGENT_INITIALS, size = 28 }) {
  const info = AGENT_INITIALS[agent] || { initials: agent.slice(0, 2).toUpperCase(), bg: '#eee', text: '#666' }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: info.bg, color: info.text,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 600, flexShrink: 0,
    }}>
      {info.initials}
    </div>
  )
}

export function Button({ children, onClick, variant = 'default', size = 'md', style, disabled }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 5, cursor: disabled ? 'not-allowed' : 'pointer',
    border: '0.5px solid', borderRadius: 8, fontWeight: 500,
    padding: size === 'sm' ? '4px 10px' : '7px 14px',
    fontSize: size === 'sm' ? 11 : 13,
    opacity: disabled ? 0.5 : 1,
  }
  const variants = {
    default: { background: 'transparent', borderColor: 'rgba(0,0,0,0.2)', color: '#333' },
    primary: { background: '#185FA5', borderColor: '#185FA5', color: '#fff' },
    success: { background: '#1D9E75', borderColor: '#1D9E75', color: '#fff' },
    danger:  { background: '#E24B4A', borderColor: '#E24B4A', color: '#fff' },
  }
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant], ...style }}>
      {children}
    </button>
  )
}

export function Input({ label, id, ...props }) {
  return (
    <div style={{ flex: 1, minWidth: 130 }}>
      {label && <label htmlFor={id} style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 3 }}>{label}</label>}
      <input id={id} style={{
        width: '100%', fontSize: 12, padding: '7px 9px',
        border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8,
        background: '#fff', color: '#111', boxSizing: 'border-box',
      }} {...props} />
    </div>
  )
}

export function Select({ label, id, children, ...props }) {
  return (
    <div style={{ flex: 1, minWidth: 130 }}>
      {label && <label htmlFor={id} style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 3 }}>{label}</label>}
      <select id={id} style={{
        width: '100%', fontSize: 12, padding: '7px 9px',
        border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8,
        background: '#fff', color: '#111',
      }} {...props}>
        {children}
      </select>
    </div>
  )
}

export function EmptyState({ children }) {
  return (
    <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#aaa', fontSize: 13 }}>
      {children}
    </div>
  )
}

export function Notification({ message, onClose }) {
  if (!message) return null
  return (
    <div style={{
      background: '#EAF3DE', color: '#3B6D11', borderRadius: 8,
      padding: '9px 14px', fontSize: 12, marginBottom: '0.85rem',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
    }}>
      <span>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3B6D11', fontSize: 16 }}>×</button>
    </div>
  )
}

export function Table({ headers, children, emptyMessage }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{
                textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#888',
                padding: '7px 8px', borderBottom: '0.5px solid rgba(0,0,0,0.1)',
                whiteSpace: 'nowrap'
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
      {!children || (Array.isArray(children) && children.length === 0) ? (
        <EmptyState>{emptyMessage || 'No data yet.'}</EmptyState>
      ) : null}
    </div>
  )
}

export function Td({ children, style }) {
  return (
    <td style={{ padding: '7px 8px', borderBottom: '0.5px solid rgba(0,0,0,0.07)', verticalAlign: 'middle', ...style }}>
      {children}
    </td>
  )
}
